import { createContext, useContext, useEffect, useState } from "react";

type ViewMode = "admin" | "coach";

interface CoachViewContextValue {
  viewMode: ViewMode;
  viewAsCoach: boolean;
  isAdmin: boolean;
  setViewMode: (mode: ViewMode) => void;
  toggleViewMode: () => void;
}

const CoachViewContext = createContext<CoachViewContextValue | undefined>(
  undefined
);

const STORAGE_KEY = "ms:coachViewMode";

export function CoachViewProvider({
  isAdmin,
  children,
}: {
  isAdmin: boolean;
  children: React.ReactNode;
}) {
  const [viewMode, setViewMode] = useState<ViewMode>("admin");

  // Load persisted mode only for admins
  useEffect(() => {
    if (!isAdmin) {
      setViewMode("admin");
      return;
    }
    try {
      const saved = localStorage.getItem(STORAGE_KEY) as ViewMode | null;
      if (saved === "coach" || saved === "admin") {
        setViewMode(saved);
      }
    } catch {
      /* ignore storage errors */
    }
  }, [isAdmin]);

  // Persist mode for admins
  useEffect(() => {
    if (!isAdmin) return;
    try {
      localStorage.setItem(STORAGE_KEY, viewMode);
    } catch {
      /* ignore storage errors */
    }
  }, [viewMode, isAdmin]);

  const toggleViewMode = () => {
    if (!isAdmin) return;
    setViewMode((prev) => (prev === "admin" ? "coach" : "admin"));
  };

  const value: CoachViewContextValue = {
    viewMode,
    viewAsCoach: isAdmin && viewMode === "coach",
    isAdmin,
    setViewMode: (mode) => {
      if (!isAdmin) return;
      setViewMode(mode);
    },
    toggleViewMode,
  };

  return (
    <CoachViewContext.Provider value={value}>
      {children}
    </CoachViewContext.Provider>
  );
}

export const useCoachView = () => {
  const ctx = useContext(CoachViewContext);
  if (!ctx) {
    throw new Error("useCoachView must be used within a CoachViewProvider");
  }
  return ctx;
};
